import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import { StatsSectionInnerProps } from 'src/Components/StatsSectionInnerBlock/components'
import { useGetPerformanceTotalsByIdQuery } from 'src/Redux/adsCampaignApi'
import { numberFormatCommas, numberToMoney } from 'src/Constants/functions'
import { variables } from 'src/Styled/variables'

import { getCostPerFollower, getCTR, getSpentAmount } from '../helpers'

import { PerformanceOverviewProps } from './PerformanceOverview'

interface UsePerformanceOverview {
  isLoading: boolean
  lastUpdatedAt?: string
  impressionsClicksCtrSections: StatsSectionInnerProps[]
  conversionsSection: StatsSectionInnerProps[]
}

const HintTitle = styled.span`
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
  color: ${({ theme }) => theme.color.darkGrey};
`

export const usePerformanceOverview = (
  props: PerformanceOverviewProps,
): UsePerformanceOverview => {
  const { campaignID } = useParams<{ campaignID: string }>()
  const { data: performanceTotalsById, isLoading } =
    useGetPerformanceTotalsByIdQuery(campaignID || '', {
      skip: !campaignID,
    })

  const spent = getSpentAmount(props)

  const costPerFollower = getCostPerFollower(
    spent,
    performanceTotalsById?.lastFollowersDelta || 0,
  )
  const costPerConversion =
    performanceTotalsById?.totalConversions &&
    performanceTotalsById.totalConversions > 0
      ? spent / performanceTotalsById.totalConversions
      : 0

  const impressionsClicksCtrSections = [
    {
      title: 'Impressions',
      value: numberFormatCommas(performanceTotalsById?.totalImpressions),
      hintText: (
        <>
          <HintTitle>Impressions</HintTitle> <br />
          The number of people who saw your ad.
        </>
      ),
    },
    {
      title: 'Ad clicks',
      value: numberFormatCommas(performanceTotalsById?.totalClicks),
      hintText: (
        <>
          <HintTitle>Ad clicks</HintTitle> <br />
          The number of people who clicked your ad.
        </>
      ),
    },
    {
      title: 'CTR',
      value: getCTR(
        performanceTotalsById?.totalClicks,
        performanceTotalsById?.totalImpressions,
      ),
      hintText: (
        <>
          <HintTitle>CTR (Click-through rate)</HintTitle> <br />
          The percentage of people who clicked the ad after seeing it.
        </>
      ),
    },
  ]

  const playlistConversionsSections = [
    {
      title: 'Conversions',
      value: numberFormatCommas(performanceTotalsById?.totalConversions),
      hintText: (
        <>
          <HintTitle>Conversions</HintTitle> <br />
          The number of people who clicked the ‘Play’ button on your smart link.
        </>
      ),
    },
    {
      title: 'Cost per conversion',
      value: `$${numberToMoney(costPerConversion)}`,
    },
    {
      title: 'Playlist followers',
      value: numberFormatCommas(performanceTotalsById?.lastFollowersDelta || 0),
    },
    {
      title: 'Cost per follower',
      value: `$${numberToMoney(costPerFollower)}`,
    },
  ]

  const trackConversionsSections = [
    {
      title: 'Conversions',
      value: numberFormatCommas(performanceTotalsById?.totalConversions),
      hintText: (
        <>
          <HintTitle>Conversions</HintTitle> <br />
          The number of people who clicked the ‘Play’ button on your smart link.
        </>
      ),
    },
    {
      title: 'Cost per conversion',
      value: `$${numberToMoney(costPerConversion)}`,
    },
  ]

  return {
    isLoading,
    impressionsClicksCtrSections,
    conversionsSection:
      props.strategy === 'playlist'
        ? playlistConversionsSections
        : trackConversionsSections,
    lastUpdatedAt: performanceTotalsById?.lastUpdatedAt,
  }
}
